import constants from '@Common/Core/Constant';
import pointerHelper from '@Common/Helpers/PointerHelper';
import { Control, IControl } from '@Common/Controls/Controls';
import { Button } from '@Common/Controls/SimpleButton';
import { IControlParameters } from '@Common/Controls/BaseControl';
import { Popup } from '@Common/Controls/Popup';
import { DropDown } from '@Common/Controls/DropDown';
import { DragAndDrop } from '@Common/Controls/DragAndDrop';
import { isDestroyable, safeDestroys } from '@Common/Core/Destroyable';

export interface IToolbarParameters extends IControlParameters {
    buttons?: IToolbarButton[];
    useText?: boolean;
    thickness?: number;
    backgroundColor?: string;
    horizontal?: boolean;
    singleValue?: boolean;
    behaveAsRadio?: boolean;
    buttonSize?: number;
    popupButtonSize?: number;
}

export interface IToolbarButton {
    padding?: number;
    inlineText?: string;
    title?: string;
    image?: string;
    enabled?: boolean;
    children?: any[];
    isSeparator?: boolean;
    toggle?: boolean;
    callback?: (value?: any) => void;
    id?: string;
    width?: number;
    useText?: boolean;
    plainText?: boolean;
    popup?: {
        css?: any;
        className?: string;
        rowsCount?: number;
        colsCount?: number;
    };
    control?: IControl;
    componentType?: any;
    converter?: any;
    custom?: boolean;

    getChildren?(id: string): IToolbarButton[];
}

export interface Toolbar extends Control {
    setButtonsState(buttonIds: any[], enabled: boolean);

    getValue(): { id: any; state: boolean }[];

    insertButton(button: IToolbarButton, index?: number);

    insertButtons(buttons: IToolbarButton[], index?: number);

    removeButton(buttonId: any);

    removeAllButtons();

    getButtons(): IToolbarButton[];

    getButton(buttonId: any): IToolbarButton;

    destroy();

    adjust(width?, height?);
}

export function Toolbar(param: IToolbarParameters): Toolbar {
    return toolbar(param);
}

export function toolbar(param: IToolbarParameters): Toolbar {
    param = $.extend(
        true,
        {
            css: {
                overflow: 'hidden'
            },
            className: '',

            buttons: [],

            buttonSize: 28,
            popupButtonSize: null,
            useText: false,
            horizontal: true,
            behaveAsRadio: false
        },
        param
    );

    if (param.popupButtonSize == null) {
        param.popupButtonSize = param.buttonSize;
    }

    let root = $('<div class="toolbar" />').addClass(param.className).css(param.css);

    const table = $('<table><thead></thead><tbody></tbody></table>').appendTo(root);

    const isHorizontal = param.horizontal;
    let buttonContainer = isHorizontal ? $('<tr />').appendTo(table) : null;
    let buttons = [];

    const dropDownWidth = 21;
    let dropDown;
    let dropDownContainer;
    if (param.useText) {
        dropDown = new DropDown({
            width: dropDownWidth,
            height: param.buttonSize,
            cloneItems: false,
            sortable: false,
            itemSelectedStyle: constants.driverDefaultStyle.itemDefaultStyle
        });
        dropDown.onItemClick(function (item) {
            param.buttons[item.buttonIndex].callback();
        });
        dropDownContainer = $('<span class="toolbar-drop-down"></span>').hide().append(dropDown.getControl()).appendTo(root);
    }

    const separatorPadding = 2;

    _(param.buttons).forEach(function (button, index: number) {
        if (index == param.buttons.length - 1 && button.isSeparator) {
            return;
        }
        insertButton(button);
    });

    function getPopupControl(childrenButtons, rowsCount) {
        const columnsCount = Math.ceil(childrenButtons.length / 4);
        const table = $('<table class="toolbar toolbar-popup" ><thead></thead><tbody></tbody></table>').css({
            backgroundColor: param.css.backgroundColor
        });

        const buttons = [];
        for (let i = 0; i < rowsCount; i++) {
            const row = $('<tr />').appendTo(table);
            for (let j = 0; j < columnsCount; j++) {
                const button = childrenButtons[j * rowsCount + i];
                if (button) {
                    const cell = $('<td />').appendTo(row);

                    let buttonControl;
                    if (!button.isSeparator) {
                        const btn = getButtonControl(button, table);
                        buttons.push(btn);
                        buttonControl = btn.getControl();
                    } else {
                        cell.addClass('toolbar-horizontal-separator').css({ cursor: 'default' });
                        buttonControl = $('<div style="width: 100%; display: block; " />');
                    }
                    cell.append(buttonControl);
                }
            }
        }

        return { table, buttons };
    }

    function insertButton(button: IToolbarButton, index?) {
        let children = button.children != null ? button.children : [];
        const hasChildren = hasButtonChildren(button);

        if (!isHorizontal) {
            buttonContainer = $('<tr />').appendTo(table);
        }

        let buttonElement;
        let buttonControl = null;
        let popupContent: { table: JQuery<HTMLElement>; buttons: Button[] };
        let isContentVisible = null;
        if (button.isSeparator) {
            const padding = (button.padding || 0) + 'px';

            buttonElement = $('<td/>')
                .addClass(isHorizontal ? 'toolbar-vertical-separator' : 'toolbar-horizontal-separator')
                .append($('<div/>').css(isHorizontal ? 'height' : 'width', param.buttonSize - separatorPadding * 2))
                .css({
                    padding: isHorizontal ? '0 ' + padding : padding + ' 0'
                });

            buttonControl = {
                getControl: function () {
                    return buttonElement;
                },
                destroy: function () {
                    buttonElement.remove();
                },
                value: $.noop
            };
        } else {
            buttonControl = getButtonControl(button);

            const buttonClick = () => {
                if (buttonControl.getState() == true) {
                    if (hasChildren) {
                        if (!isContentVisible) {
                            if (button.getChildren) {
                                children = button.getChildren(button.id);
                            }
                            if (children.length) {
                                const { rowsCount = 5, colsCount = 0 } = button.popup ?? {};
                                if (colsCount > 0) {
                                    popupContent = getPopupControl(children, children.length / colsCount);
                                } else {
                                    popupContent = getPopupControl(children, rowsCount || 5);
                                }

                                if (button.popup) {
                                    button.popup.css && popupContent.table.css(button.popup.css);
                                    button.popup.className && popupContent.table.addClass(button.popup.className);
                                }
                                const buttonOffset = buttonElement.offset();
                                const popupControl = new Popup({
                                    x: buttonOffset.left,
                                    y: buttonOffset.top + buttonElement.outerHeight(true),
                                    content: popupContent.table,
                                    reusable: false
                                });
                                popupControl.show();
                                popupControl.onClose(() => {
                                    popupContent.buttons.forEach(btn => {
                                        btn.destroy();
                                    });
                                    popupControl.destroy();
                                });
                            }
                        }
                    } else {
                        if (button.toggle && param.behaveAsRadio) {
                            _(buttons).forEach(function (item) {
                                item.id != button.id && item.control && item.control.value(false);
                            });
                        }
                        button.callback && button.callback(buttonControl.value());
                    }
                }
            };

            const btnControl = button.custom
                ? button.control.getControl
                    ? button.control.getControl()
                    : button.control
                : buttonControl.getControl();

            buttonElement = $('<td />').append(btnControl);

            if (hasChildren) {
                buttonElement.click(buttonClick);
            }

            buttonElement.on(pointerHelper.pointerDownEventName, function () {
                isContentVisible = popupContent && popupContent.table.is(':visible');
            });

            buttonControl.onClick(buttonClick);
            buttonControl.onDestroy(function () {
                buttonElement.remove();
            });
        }

        const buttonObject = {
            control: buttonControl,
            id: button.id,
            isSeparator: button.isSeparator,
            customControl: undefined
        };

        if (button.custom) {
            buttonObject.customControl = button.control;
        }

        if (index === undefined || index < 0 || index >= buttons.length) {
            buttons.push(buttonObject);
            buttonContainer.append(buttonElement);
        } else {
            buttons[index].control.getControl().parent().before(buttonElement);
            buttons.splice(index, 0, buttonObject);
        }
    }

    function getButtonControl(button, popupControl?) {
        const hasChildren = hasButtonChildren(button);

        const useText = button.useText === undefined ? param.useText || button.plainText : button.useText;
        const buttonSize = popupControl ? param.popupButtonSize : param.buttonSize;
        const defaultSize = useText ? 'auto' : buttonSize;
        const width = button.width || (isHorizontal ? defaultSize : buttonSize);

        const buttonControl = new Button({
            width: hasChildren ? width + 10 : width,
            height: button.height || (!isHorizontal ? defaultSize : buttonSize),

            tooltip: button.title,
            icon: button.image,
            text: useText ? button.title : '',

            textColor: param.css.color,
            backgroundColor: param.css.backgroundColor,
            borderRadius: 0,

            toggle: button.toggle,
            enabled: button.enabled,
            hover: true,
            className: hasChildren ? 'button-menu' : ''
        });

        if (button.value) {
            buttonControl.value(button.value);
        }

        function refreshToggle() {
            if (button.toggle && button.toggleImage) {
                buttonControl.setImage(buttonControl.value() ? button.toggleImage : button.image);
            }
        }

        refreshToggle();
        buttonControl.onChange(refreshToggle);

        if (button.canDrag) {
            const dragControl = new DragAndDrop({
                canDrag: true,
                showContainer: false,
                scopes: ['toolbar-editor']
            });
            dragControl.drag(buttonControl.getControl(), button);
            dragControl.onPreDrag(function (element, toolbarButton) {
                popupControl && popupControl.hide();
                setButtonOnTop(toolbarButton);
                return {
                    uiObjects: buttonControl.getControl().removeClass('button-hover')
                };
            });
            buttonControl.tag(dragControl);
        }
        button.callback &&
            buttonControl.onClick(function () {
                button.callback(buttonControl.value());
                popupControl && popupControl.hide();
            });

        return buttonControl;
    }

    function setButtonOnTop(button) {
        const parentButton = _.find(param.buttons, item => _.some(item.children, childButton => childButton.id == button.id));

        if (parentButton) {
            const buttonObject = getButton(parentButton.id);
            if (buttonObject) {
                buttonObject.control.setImage(button.image);
                buttonObject.control.setTooltip(button.title);
                buttonObject.id = parentButton.id = button.id;
                parentButton.componentType = button.componentType;
                parentButton.image = button.image;
                parentButton.title = button.title;
                parentButton.converter = button.converter;
            }
        }
    }

    function setButtonsState(buttonIds, state) {
        _(buttonIds).forEach(function (buttonId) {
            const button = findButton(buttonId);
            button && button.control.setState(state);
            dropDown &&
                dropDown.updateItem(buttonId, {
                    disable: !state
                });
        });
    }

    function findButton(buttonId) {
        return _.find(buttons, function (item) {
            return item.id == buttonId;
        });
    }

    function removeButton(buttonId) {
        const button = findButton(buttonId);
        if (button) {
            button.control.destroy();
            buttons.remove(button);
        }
    }

    function removeAllButtons() {
        (isHorizontal ? buttonContainer : table).empty();
        buttons.forEach(btn => {
            if (btn.control instanceof Button) {
                const drag = btn.control.tag();
                drag && drag.destroy();
            }
            isDestroyable(btn.control) && btn.control.destroy();
        });
        buttons.length = 0;
    }

    function insertButtons(newButtons, index) {
        _(newButtons).forEach(function (button) {
            insertButton(button, index);
        });
    }

    function getValue() {
        return _(buttons)
            .filter(function (button) {
                return button.control && !button.isSeparator;
            })
            .map(function (button) {
                return {
                    id: button.id,
                    state: button.control.value()
                };
            })
            .value();
    }

    function getButton(buttonId) {
        return _.find(buttons, function (button) {
            return button.id == buttonId;
        });
    }

    function setState(enabled) {
        _(buttons).forEach(function (button) {
            !button.isSeparator && button.control.setState(enabled);
        });
        dropDown && dropDown.setState(enabled);
        root.toggleClass('disabled', !enabled);
    }

    function hasButtonChildren(button) {
        const children = button.children != null ? button.children : [];
        return !!(children.length || button.getChildren);
    }

    function adjustButtons(secondPass?) {
        if (!param.useText) {
            return;
        }

        let buttonControl, button;
        let rootWidth = root.outerWidth(true);
        let innerWidth = table.outerWidth(true);
        let currentButton;

        if (innerWidth > rootWidth) {
            table.css({
                display: 'inline-table'
            });
            if (dropDown) {
                dropDown.setItems([]);
                dropDownContainer.show();
            }

            currentButton = buttons.length - 1;
            while (innerWidth > rootWidth && currentButton > -1) {
                button = buttons[currentButton];
                buttonControl = button.control;
                buttonControl.getControl().hide();

                if (!button.isSeparator && dropDown) {
                    dropDown.prependItems([
                        {
                            id: param.buttons[currentButton].id,
                            name: param.buttons[currentButton].title,
                            image: param.buttons[currentButton].image,
                            buttonIndex: currentButton,
                            disable: !buttonControl.getState()
                        }
                    ]);
                }
                innerWidth = table.outerWidth(true);
                rootWidth = root.outerWidth(true) - dropDownWidth;
                currentButton--;
            }
        } else {
            table.css({
                display: 'initial'
            });
            dropDownContainer.hide();

            currentButton = 0;
            while (innerWidth < rootWidth && currentButton < buttons.length) {
                buttons[currentButton].control.getControl().show();
                innerWidth = table.outerWidth(true);
                currentButton++;
            }

            !secondPass && adjustButtons(true);
        }
    }

    return {
        getControl: function () {
            return root;
        },
        setButtonsState: setButtonsState,
        getValue: getValue,
        insertButton: insertButton,
        insertButtons: insertButtons,
        removeButton: removeButton,
        removeAllButtons: removeAllButtons,
        getButtons: function () {
            return buttons;
        },
        getButton: getButton,
        setState: setState,
        destroy: function () {
            if (buttons) {
                removeAllButtons();
                buttons = null;
            }
            safeDestroys(root, dropDown);
            root = null;
            param = null;
        },
        adjust: function (width?, height?) {
            adjustButtons();
        }
    };
}
